import {defineStore} from 'pinia'
import axios from '@/api/axios'
import {IMPERSONATION_TOKEN_KEY} from '@shared/constants/auth'
import {authActions} from '@shared-utils/auth/authActions'

export const useAuthenticationStore = defineStore('authenticationStore', {
  state: () => ({
    isAuthenticated: false,
    user: {},
    performanceReportAvailability: {},
  }),
  getters: {
    authUser: state => {
      return state.user
    },
    userCompanyHash: state => {
      return state.user?.company?.hash
    },
    userHasCompany: state => {
      return !!state.user?.company
    },
    isDemoCompany: state => {
      return state.user?.company?.hash === 'lbMDVBRx' || false
    },
    userUnitId: state => {
      return state.user?.unit?.id
    },
    isInternalUser: state => {
      return state.user.policies?.is_internal || false
    },
    isCoreTeam: state => {
      return state.user.policies?.is_core_team || false
    },
    businessUnit: state => {
      return state.user.policies?.business_unit || false
    },
    isComplianceManager: state => {
      return state.user.policies?.is_compliance || false
    },
    isComplianceTeamLead: state => {
      return state.user.policies?.is_compliance_team_lead || false
    },
    isMediaPlanner: state => {
      return state.user.policies?.is_media_planner || false
    },
    isCSM: state => {
      return state.user.policies?.is_csm || false
    },
    isMediaPlannerOrCSM: state => {
      return state.user.policies?.is_media_planner || state.user.policies?.is_csm
    },
    managerDirection: state => {
      return state.user.policies?.manager_direction || null
    },
    isTychko: state => {
      return state.user.email === 'oleksandr.t@point2web.com' || false
    },
    isAlexKosovskiy: state => {
      return state.user.email === 'alexander.k@point2web.com' || false
    },
    isRizhnyak: state => {
      return state.user.email === 'bohdan.r@point2web.com' || false
    },
    isAgency: state => {
      return state.user.policies?.is_agency || false
    },
    isAgencyHeads: state => {
      return state.user.policies?.is_agency_heads || false
    },
    canEditAgencyHeadNote: state => {
      return state.user.policies?.can_edit_agency_head_note || false
    },
    canAccessWeeklyClientsUpdate: state => {
      return state.user.policies?.can_access_weekly_clients_update || false
    },
    isMediaBuyer: state => {
      return state.user.policies?.is_media_buyer || false
    },
    isMediaBuyerTL: state => {
      return (state.user.policies?.is_media_buyer || false) && (state.user.is_manager || false)
    },
    isManager: state => {
      return !!state.user?.is_manager || false
    },
    isSaleManager: state => {
      return state.user.policies?.is_sale_manager || false
    },
    isOnboardingManager: state => {
      return state.user.policies?.is_onboarding_manager || false
    },
    isSaleManagerTeamLead: state => {
      return state.user.policies?.is_sale_manager_team_lead || false
    },
    isSuperAdmin: state => {
      return state.user.policies?.is_super_admin || false
    },
    isTechSupportOrQA: state => {
      return state.user.policies?.is_tech_support_or_qa || false
    },
    canAudit: state => {
      return state.user.policies?.can_audit || false
    },
    canEditEmailSignatureAvatar: state => {
      return state.user.policies?.can_edit_email_signature_avatar || false
    },
    isHomeImprovementUnit: state => {
      return state.user.policies?.is_home_improvement_unit || false
    },
    isHomeInsuranceUnit: state => {
      return state.user.policies?.is_home_insurance_unit || false
    },
    isHRUnit: state => {
      return state.user?.employee?.unit_name === 'HR' || false
    },
    isCopywriterUnit: state => {
      return state.user?.employee?.unit_name === 'Copywriting' || false
    },
    isDesignUnit: state => {
      return state.user?.employee?.unit_name === 'Design' || false
    },
    isPerformaceNetworkDepartment: state => {
      return state.user?.employee?.department === 'Performance Network' || false
    },
    isBusinessUnitAgency: state => {
      return state.user.policies?.business_unit === 'agency' || false
    },
    isAgencyOrSupportBusinessUnit: state => {
      return ['agency', ''].includes(state.user.policies?.business_unit)
    },
    isAgencyOrSupportOrApacBusinessUnit: state => {
      return ['agency', '', 'apac'].includes(state.user.policies?.business_unit)
    },
    isBusinessUnitHomeIQ: state => {
      return state.user.policies?.business_unit === 'home_iq' || false
    },
    isBusinessUnitAPAC: state => {
      return state.user.policies?.business_unit === 'apac' || false
    },
    isSupportUnitEmployee: state => {
      return state.user.policies?.business_unit === '' || false
    },
    hasAccessToCompaniesPage: state => {
      return state.user.policies?.has_access_to_companies_page || false
    },
    hasAccessToSupportToolsPage: state => {
      return state.user.policies?.has_access_to_support_tools_page || false
    },
    canOpenAdAccountReport: state => {
      return state.user.policies?.can_open_ad_account_report || false
    },
    hasAccessToTopUps: state => {
      return (
        state.user.policies?.is_finance ||
        state.user.policies?.is_agency ||
        state.user.company?.top_ups_enabled ||
        false
      )
    },
    isAgencyAssistance: state => {
      return state.user.policies?.is_agency_assistance || false
    },
    isAgencyAssistanceTeamLead: state => {
      return state.user.policies?.is_agency_assistance_team_lead || false
    },
    isCreditCardManager: state => {
      return state.user.policies?.is_credit_card_manager || false
    },
    isAgencyClient: state => {
      return state.user.policies?.is_agency_client || false
    },
    isFinance: state => {
      return state.user.policies?.is_finance || false
    },
    isDeveloper: state => {
      return state.user.policies?.is_developer || false
    },
    isDeveloperOrInternalCompany: state => {
      return state.user.policies?.is_developer_or_internal_company || false
    },
    isTechDepartment: state => {
      return state.user?.employee?.department === 'Tech' || false
    },
    isAnalyst: state => {
      return state.user.policies?.is_analyst || false
    },
    accessToFacebookLicenses: state => {
      return state.user.policies?.access?.facebook_licenses || false
    },
    canUseTopUpAsap: state => {
      return state.user.policies?.asap_top_up_enabled || false
    },
    canUseDomains: state => {
      return state.user.policies?.can_use_domains || false
    },
    canUseAppeals: state => {
      return state.user.policies?.can_use_appeals || false
    },
    canModerateClientPreModeration: state => {
      return state.user.policies?.can_moderate_client_pre_moderation || false
    },
    canUseSnapchatAutoCreate: state => {
      return state.user.policies?.can_use_snapchat_auto_create || false
    },
    canUseTiktokAutoCreate: state => {
      return state.user.policies?.can_use_tiktok_auto_create || false
    },
    canUseGoogleAutoCreate: state => {
      return state.user.policies?.can_use_google_auto_create || false
    },
    canUseNewsbreakAutoCreate: state => {
      return state.user.policies?.can_use_newsbreak_auto_create || false
    },
    canUseInvoices: state => {
      return state.user.policies?.can_use_invoices || false
    },
    canUseRetainers: state => {
      return state.user.policies?.can_use_retainers || false
    },
    canEditRetainerConditions: state => {
      return state.user.policies?.can_edit_retainer_conditions || false
    },
    canUseCoupons: state => {
      return state.user.policies?.can_use_coupons || false
    },
    canSeeFBPageRequestForm: state => {
      return state.user.policies?.can_view_fb_page_requests_form || false
    },
    canWorkWithCreatives: state => {
      return state.user.policies?.can_work_with_creatives || false
    },
    canWorkWithOnboardings: state => {
      return state.user.policies?.can_work_with_onboardings || false
    },
    isLegal: state => {
      return state.user.policies?.is_legal || false
    },
    isLegalOrFinance: state => {
      return state.user.policies?.is_finance || state.user.policies?.is_legal || false
    },
    isPoint2WebEmployee: state => {
      return state.user.policies?.is_point2web_employee || false
    },
    isPoint2WebMarketing: state => {
      return state.user.policies?.is_point2web_marketing || false
    },
    canCreateInternalTask: getters => {
      return getters.isComplianceManager
    },
    fromSFCompany: state => {
      return state.user.policies?.from_sf_company || false
    },
    fromLGCompany: state => {
      return state.user.policies?.from_lg_company || false
    },
    hasTwoFA: state => {
      return state.user.two_fa_enabled || false
    },
    hasAccessToUsersPage: state => {
      return state.user.policies?.has_access_to_users_page || false
    },
    hasAccessToOverdrafts: state => {
      return state.user.policies?.has_access_to_overdrafts || false
    },
    hasAccessToRequestsPage: state => {
      return state.user.policies?.has_access_to_requests_page || false
    },
    hasAccessToProformaInvoices: state => {
      return state.user.policies?.has_access_to_proforma_invoices || false
    },
    hasAccessToAssetsPage: state => {
      return state.user.policies?.has_access_to_assets_page || false
    },
    isCompanyAdmin: state => {
      return (state.user?.company?.hash && state.user?.subrole === 'admin') || false
    },
    isSubroleMember: state => state.user?.subrole === 'member' || false,
    canUseUserFilter: getters => !getters.isSubroleMember,

    userBalanceStatus: state => state.user?.company?.balance_info?.remaining_balance_status || null,
    userRemainingBalanceDays: state => state.user?.company?.balance_info?.remaining_balance_days || null,
    userAvailableBalance: state => state.user?.company?.balance_info?.available_balance || null,

    canSeeFacebookPages: getters => {
      return getters.isAgencyAssistance || getters.isDeveloper
    },

    canCreateAgencyGeneralTask: getters => {
      return (
        getters.isMediaPlannerOrCSM ||
        getters.isComplianceManager ||
        getters.isSaleManager ||
        getters.isBusinessUnitAPAC
      )
    },

    canUseCreditLineList: getters => {
      return getters.isSaleManager || getters.isSaleManagerTeamLead || getters.isDeveloper
    },

    canCreateShortTask: getters => {
      return getters.isAgency || getters.isBusinessUnitAPAC
    },

    canCreateNewLandingTask: getters => {
      return getters.isDeveloper
    },

    canCreateCopyLandingTask: getters => {
      return getters.isDeveloper
    },

    canCreateABtestTask: getters => {
      return getters.isDeveloper
    },

    canCreateCreativeTask: getters => {
      return getters.isAgency || getters.isBusinessUnitAPAC
    },
    canCreateSMMTask: getters => {
      return (
        getters.isPoint2WebMarketing || getters.isAgency || (getters.isBusinessUnitAPAC && !getters.isAgencyAssistance)
      )
    },
    canAddUserToPointboard: state => {
      return state.user.policies?.can_add_user_to_pointboard || false
    },
    canManageEmployees: state => {
      return state.user.policies?.can_manage_employees || false
    },
    isGoogleUser: getters => {
      return getters.authUser.email === 'google@point2web.com'
    },
    requestsRouteName: getters => {
      if (getters.isFinance) {
        return 'ad-account-requests.top-up.list'
      }

      if (!getters.isPoint2WebEmployee) {
        return 'my-requests.list'
      }

      return 'ad-account-requests.accounts.list'
    },
    isImpersonated: () => {
      return !!sessionStorage.getItem(IMPERSONATION_TOKEN_KEY)
    },
  },
  actions: {
    ...authActions({axios}),
    fetchPerformanceReportAvailability: async function () {
      try {
        const {data} = await axios.get('/client-dashboard/performance-reports/availability')
        this.performanceReportAvailability = data
      } catch (e) {
        console.error(e)
      }
    },
  },
})
